import React from "react";
import { CommonInput } from "@/components/ui/common-input";
import { CommonSelect } from "@/components/ui/common-select";
import { CommonButton } from "@/components/ui/common-button";
import { Trash2, Info, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import GoogleMapSelector from "@/components/tripManagement/addtrip/GoogleMapSelector";

const SecondaryDispatchItemCard = ({
  row,
  idx,
  rowErrors,
  destinationTypeOptions,
  states = [],
  districtsByState = {},
  distributorsByDistrict = {},
  retailersByDistrict = {},
  loadingDropdowns = {},
  loadingDistricts = {},
  loadingDistributors = {},
  loadingRetailers = {},
  godowns = [],
  parentLineOptions,
  productTypes,
  updateItemRow,
  removeItemRow,
  balanceData,
  parentTrip,
  showDeleteButton,
}) => {
  const getLineRemainingQty = (sourceTripItemId) => {
    if (!sourceTripItemId || !balanceData?.lines) return null;
    const line = balanceData.lines.find((l) => l.trip_item_id === sourceTripItemId);
    return line ? line.remaining_qty_mt : null;
  };

  const lineRemaining = getLineRemainingQty(row.source_trip_item_id);

  const stateOptions = states.map((s) => ({ value: s.id, label: s.name }));
  const districtOptions = (districtsByState[row.state_id] || []).map((d) => ({
    value: d.id,
    label: d.name,
  }));
  const distributorOptions = (distributorsByDistrict[row.district_id] || []).map((d) => ({
    value: d.id,
    label: d.company_name || d.name,
  }));
  const retailerOptions = (retailersByDistrict[row.district_id] || []).map((r) => ({
    value: r.id,
    label: r.company_name || r.username || r.name,
  }));

  const isLoadingStates = loadingDropdowns.states || false;
  const isLoadingDistricts = loadingDistricts[row.state_id] || false;
  const isLoadingDistributors = loadingDistributors[row.district_id] || false;
  const isLoadingRetailers = loadingRetailers[row.district_id] || false;

  const showStateDistrict = row.destinationType === "distributor" || row.destinationType === "retailer";

  return (
    <div className="relative p-4 rounded-lg border border-border bg-card space-y-4">
      <div className="flex justify-between items-center border-b pb-2 border-border/50">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest bg-secondary/60 px-2.5 py-0.5 rounded-md">
          Item #{idx + 1}
        </span>
        {showDeleteButton && (
          <CommonButton
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeItemRow(row.id)}
            className="text-destructive hover:bg-destructive/10 h-8 w-8 rounded-md transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </CommonButton>
        )}
      </div>

      <div className="space-y-4">
        {/* Row 1: Destination Type and Targets */}
        <div className={cn(
          "grid gap-4 items-start grid-cols-1",
          showStateDistrict ? "md:grid-cols-4" : "md:grid-cols-2"
        )}>
          <div>
            <CommonSelect
              label="Destination"
              id={`dest_type_${row.id}`}
              value={row.destinationType}
              onValueChange={(val) => updateItemRow(row.id, "destinationType", val)}
              options={destinationTypeOptions}
              required
            />
          </div>

          {showStateDistrict && (
            <>
              <div>
                <CommonSelect
                  label="State"
                  id={`state_${row.id}`}
                  value={row.state_id}
                  onValueChange={(val) => updateItemRow(row.id, "state_id", val)}
                  options={stateOptions}
                  error={rowErrors.state_id}
                  required
                  searchable
                  placeholder="Select state"
                  loading={isLoadingStates}
                />
              </div>

              <div>
                <CommonSelect
                  label="District"
                  id={`district_${row.id}`}
                  value={row.district_id}
                  onValueChange={(val) => updateItemRow(row.id, "district_id", val)}
                  options={districtOptions}
                  error={rowErrors.district_id}
                  required
                  searchable
                  placeholder={row.state_id ? "Select district" : "Select state first"}
                  disabled={!row.state_id}
                  loading={isLoadingDistricts}
                />
              </div>
            </>
          )}

          <div>
            {row.destinationType === "distributor" && (
              <CommonSelect
                label="Distributor"
                id={`dist_${row.id}`}
                value={row.distributor_id}
                onValueChange={(val) => updateItemRow(row.id, "distributor_id", val)}
                options={distributorOptions.length > 0 ? distributorOptions : (row.district_id ? [{ value: "no-data", label: "No distributors found", disabled: true }] : [])}
                error={rowErrors.target_id}
                required
                searchable
                placeholder={row.district_id ? "Select distributor" : "Select district first"}
                disabled={!row.district_id}
                loading={isLoadingDistributors}
              />
            )}
            {row.destinationType === "retailer" && (
              <CommonSelect
                label="Retailer"
                id={`ret_${row.id}`}
                value={row.retailer_id}
                onValueChange={(val) => updateItemRow(row.id, "retailer_id", val)}
                options={retailerOptions.length > 0 ? retailerOptions : (row.district_id ? [{ value: "no-data", label: "No retailers found", disabled: true }] : [])}
                error={rowErrors.target_id}
                required
                searchable
                placeholder={row.district_id ? "Select retailer" : "Select district first"}
                disabled={!row.district_id}
                loading={isLoadingRetailers}
              />
            )}
            {row.destinationType === "godown" && (
              <CommonSelect
                label="Godown"
                id={`godown_${row.id}`}
                value={row.rake_point_id}
                onValueChange={(val) => updateItemRow(row.id, "rake_point_id", val)}
                options={godowns.map((g) => ({ value: g.id, label: g.name }))}
                error={rowErrors.target_id}
                required
                searchable
                placeholder="Select godown"
              />
            )}
          </div>
        </div>

        {/* Row 2: Product & Quantities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div>
            <CommonSelect
              label="Select RAKE Allocation"
              id={`parent_line_${row.id}`}
              value={row.source_trip_item_id}
              onValueChange={(val) => {
                const updates = { source_trip_item_id: val };
                const matchItem = parentTrip?.items?.find((i) => i.id === val);
                if (matchItem) {
                  updates.product_type_id = matchItem.product_type_id;
                }
                updateItemRow(row.id, updates);
              }}
              options={parentLineOptions}
              placeholder="Select parent allocation"
            />
            {lineRemaining !== null && (
              <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 mt-1.5 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/20 px-2 py-0.5 rounded-md w-fit border border-emerald-100 dark:border-emerald-900/40">
                <Info className="w-3 h-3" /> Available: {lineRemaining.toFixed(2)} MT
              </span>
            )}
          </div>

          <div>
            <CommonSelect
              label="Product Type"
              id={`product_${row.id}`}
              value={row.product_type_id}
              onValueChange={(val) => updateItemRow(row.id, "product_type_id", val)}
              options={productTypes.map((p) => ({ value: p.id, label: p.name }))}
              error={rowErrors.product_type_id}
              required
              placeholder="Select product"
              disabled={!!row.source_trip_item_id}
            />
          </div>

          <div>
            <CommonInput
              label="Qty (MT)"
              id={`qty_${row.id}`}
              type="number"
              step="0.01"
              value={row.product_quantity}
              onChange={(e) => updateItemRow(row.id, "product_quantity", e.target.value)}
              error={rowErrors.product_quantity}
              required
              placeholder="0.00 MT"
            />
          </div>
        </div>

        {/* Consignee Details */}
        <div className="border border-border rounded-lg p-4 space-y-4 bg-muted/30">
          <h4 className="text-sm font-medium text-foreground border-b pb-2">Consignee (Ship To) Details</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CommonInput
              label="Consignee Name"
              id={`consignee_name_${row.id}`}
              value={row.consignee_name || ""}
              onChange={(e) => updateItemRow(row.id, "consignee_name", e.target.value)}
              error={rowErrors.consignee_name}
              required
              placeholder="Enter consignee name"
            />
            <CommonInput
              label="Contact Person (Optional)"
              id={`consignee_contact_person_${row.id}`}
              value={row.consignee_contact_person || ""}
              onChange={(e) => updateItemRow(row.id, "consignee_contact_person", e.target.value)}
              placeholder="Enter contact person name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CommonInput
              label="Phone Number"
              id={`consignee_phone_${row.id}`}
              type="tel"
              value={row.consignee_phone || ""}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                updateItemRow(row.id, "consignee_phone", val);
              }}
              error={rowErrors.consignee_phone}
              required
              placeholder="10-digit phone number"
              maxLength={10}
            />
            <CommonInput
              label="Email (Optional)"
              id={`consignee_email_${row.id}`}
              type="email"
              value={row.consignee_email || ""}
              onChange={(e) => updateItemRow(row.id, "consignee_email", e.target.value)}
              placeholder="Enter email address"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CommonInput
              label="Invoice Number"
              id={`invoice_number_${row.id}`}
              value={row.invoice_number || ""}
              onChange={(e) => updateItemRow(row.id, "invoice_number", e.target.value)}
              error={rowErrors.invoice_number}
              required
              placeholder="Enter invoice number"
            />
            <CommonInput
              label="DC Number"
              id={`dc_number_${row.id}`}
              value={row.dc_number || ""}
              onChange={(e) => updateItemRow(row.id, "dc_number", e.target.value)}
              error={rowErrors.dc_number}
              required
              placeholder="Enter DC number"
            />
            <CommonInput
              label="Estimated Kms (Optional)"
              id={`estimated_kms_${row.id}`}
              type="number"
              value={row.estimated_kms || ""}
              onChange={(e) => updateItemRow(row.id, "estimated_kms", e.target.value)}
              placeholder="Enter estimated km"
              min="0"
              step="0.1"
            />
          </div>

          <CommonInput
            label="Address"
            id={`consignee_address_${row.id}`}
            value={row.consignee_address || ""}
            onChange={(e) => updateItemRow(row.id, "consignee_address", e.target.value)}
            error={rowErrors.consignee_address}
            required
            placeholder="Enter full address"
          />

          <div className="pt-3 border-t border-border/50">
            <GoogleMapSelector
              title="Select End Location *"
              onLocationSelect={(location) => {
                updateItemRow(row.id, {
                  end_latitude: location.lat.toString(),
                  end_longitude: location.lng.toString(),
                });
              }}
              initialLat={row.end_latitude ? parseFloat(row.end_latitude) : null}
              initialLng={row.end_longitude ? parseFloat(row.end_longitude) : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecondaryDispatchItemCard;
